import { Component, Input, OnDestroy } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { Subscription } from 'rxjs';

import { SlideToggleColorDirective } from '../../directives/slide-toggle-color.directive';

/**
 * Component representing a toggle switch with Angular reactive forms integration.
 * Implements ControlValueAccessor to integrate with Angular forms.
 */
@Component({
  selector: 'ctr-toggle-option',
  imports: [SlideToggleColorDirective, MatSlideToggleModule, ReactiveFormsModule],
  templateUrl: './toggle-option.component.html',
  styleUrls: ['./toggle-option.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: ToggleOptionComponent,
      multi: true,
    },
  ],
})
export class ToggleOptionComponent implements ControlValueAccessor, OnDestroy {
  /**
   * Optional label for the toggle switch.
   */
  @Input() label?: string;

  /**
   * Form control associated with the toggle switch.
   */
  control = new FormControl();

  /**
   * Callback function that is called when the control's value changes.
   */
  private onChange?: (value: boolean) => void;

  /**
   * Callback function that is called when the control is touched.
   */
  private onTouched?: () => void;

  /**
   * Subscription to manage and unsubscribe from valueChanges.
   */
  private valueChangesSubscription: Subscription = new Subscription();

  /**
   * Writes a new value to the form control.
   * @param obj The value to write to the control.
   */
  writeValue(obj: boolean): void {
    this.control.setValue(obj, { emitEvent: false });
  }

  /**
   * Registers a callback function to be called when the value of the control changes.
   * @param fn The callback function to register.
   */
  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
    this.valueChangesSubscription = this.control.valueChanges.subscribe((value) => {
      this.onChange?.(value);
      this.onTouched?.();
    });
  }

  /**
   * Registers a callback function to be called when the control is touched.
   * @param fn The callback function to register.
   */
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * Sets the disabled state of the control.
   * @param isDisabled Boolean indicating whether the control should be disabled.
   */
  setDisabledState?(isDisabled: boolean): void {
    isDisabled ? this.control.disable({ emitEvent: false }) : this.control.enable({ emitEvent: false });
  }

  /**
   * Cleans up the component by unsubscribing from subscriptions.
   */
  ngOnDestroy(): void {
    this.valueChangesSubscription.unsubscribe();
  }
}
